import { ActionButton, FormInputRow, Input } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { Box, Collapse, Divider, Grid, List, ListItem, ListItemText, Switch, Typography, useTheme } from '@mui/material'
import { KeyboardArrowRight } from '@mui/icons-material'
import React, { useState, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useFeatureFormContext } from './feature-form-context'
import { useStyles } from './style'
import { useDispatch } from 'react-redux'
import { BannerStatic } from '../../../components'
import { TenantEditSlice } from '../../../reducers'

const DegreedSwitchInput = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const dispatch = useDispatch()
  const { tenant } = useFeatureFormContext()
  const form = useForm({ mode: 'onChange' })
  const { getValues, setValue, trigger, formState: { errors, isValid } } = form

  const [isLoading, setIsLoading] = useState(false)
  const [checked, setChecked] = useState(!!tenant.features?.degreedIntegration?.enabled)
  const [isCollapsed, setIsCollapsed] = useState(false)

  const handleSwitch = () => setChecked(checked => {
    if (checked) setIsCollapsed(false)
    return !checked
  })
  const handleClickListItem = () => setIsCollapsed(isCollapsed => !isCollapsed)

  const [tenantUpdate] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (tenant) => {
        BannerStatic.show({ label: 'Degreed configuration updated' })
        setIsLoading(false)
        dispatch(TenantEditSlice.actions.updateTenantFeatures(tenant.features))
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error saving the toolkit features configuration',
          isError: true
        })
        setIsLoading(false)
      }
    }
  ])

  const [degreedCleanUp] = useEvent([
    {
      eventName: 'degreedCleanUp',
      onSuccess: () => {
        BannerStatic.show({ label: 'Content being cleaned up from Degreed, the process may take hours to complete.' })
        setIsLoading(false)
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error executing the command.',
          isError: true
        })
        setIsLoading(false)
      }
    }
  ])

  const [degreedPopulate] = useEvent([
    {
      eventName: 'degreedPopulate',
      onSuccess: () => {
        BannerStatic.show({ label: 'All content is being synchronized to Degreed, the process may take hours to complete.' })
        setIsLoading(false)
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error executing the command.',
          isError: true
        })
        setIsLoading(false)
      }
    }
  ])

  const handleSave = () => {
    tenantUpdate.trigger({
      pk: tenant.pk,
      features: {
        ...tenant.features,
        degreedIntegration: {
          ...getValues(),
          enabled: checked
        }
      }
    })
    setIsLoading(true)
  }

  const handleClear = () => {
    degreedCleanUp.trigger({ tenantPk: tenant.pk })
    setIsLoading(true)
  }

  const handleSynch = () => {
    degreedPopulate.trigger({ tenantPk: tenant.pk })
    setIsLoading(true)
  }

  const isSaveDisabled = useMemo(() => isLoading || (!isValid && checked), [isLoading, isValid, checked])
  const areActionsDisabled = useMemo(() => isLoading || !checked || !isValid, [isLoading, isValid, checked])

  // Update form state
  useEffect(() => {
    if (tenant.features?.degreedIntegration) {
      Object.entries(tenant.features.degreedIntegration).forEach(([name, value]) => setValue(name, value))
      trigger()
    }
  }, [tenant.features, setValue, trigger])

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box
          border={1}
          borderRadius='undefinedpx'
          borderColor='#E0E0E0'
          style={{ padding: spacing(1, 4) }}
        >
          <Box
            display='flex'
            style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <Typography variant='body2' style={{ marginTop: spacing(3.5) }}>
              Degreed Integration
            </Typography>
            <Box display='flex' style={{ justifyContent: 'flex-end', alignItems: 'flex-start', padding: spacing(1) }}>
              <Box visibility={checked ? 'visible' : 'hidden'}>
                <List component='nav' aria-label='Device settings'>
                  <ListItem
                    button
                    aria-haspopup='true'
                    aria-controls='lock-menu'
                    aria-label='when device is locked'
                    onClick={handleClickListItem}
                  >
                    <ListItemText disableTypography style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant='body2'>Configure credentials</Typography>
                      <KeyboardArrowRight />
                    </ListItemText>
                  </ListItem>
                </List>
              </Box>
              {checked && (
                <Divider
                  orientation='vertical'
                  flexItem
                  style={{
                    margin: spacing(3, 0),
                    borderColor: 'rgba(0, 0, 0, 0.38)'
                  }}
                />
              )}
              <Box style={{ padding: spacing(2, 0.5) }}>
                <Switch
                  color='primary'
                  checked={checked}
                  onChange={handleSwitch}
                  disabled={isLoading}
                />
              </Box>
            </Box>

          </Box>
          <Collapse in={isCollapsed}>
            <Box className={classes.formCtn}>
              <Grid item xs={12}>
                <FormInputRow
                  title='Client ID'
                  description='The ID of the client that is requesting access to the API. This is provided by Degreed.'
                  smallFormTitles
                  InputComponent={
                    <Input
                      label='Client ID'
                      name='clientId'
                      control={form.control}
                      rules={{ required: true }}
                      valid={errors.clientId === undefined}
                      error='Client Id is required'
                      showErrors
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='Client Secret'
                  description='The secret of the client that is requesting access to the API. This is provided by Degreed.'
                  smallFormTitles
                  InputComponent={
                    <Input
                      label='Client Secret'
                      name='clientSecret'
                      control={form.control}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='Grant Type'
                  description='The type of grant that is being requested. This is provided by Degreed.'
                  smallFormTitles
                  InputComponent={
                    <Input
                      label='Grant Type'
                      name='grantType'
                      defaultValue='client_credentials'
                      control={form.control}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='Scope'
                  description='The scope of the access that is being requested. This is provided by Degreed.'
                  smallFormTitles
                  InputComponent={
                    <Input
                      label='Scope'
                      name='scope'
                      defaultValue='content:read,content:write'
                      control={form.control}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='Base Auth URL'
                  description='The base URL for the authentication API. This is provided by Degreed.'
                  smallFormTitles
                  InputComponent={
                    <Input
                      label='Base Auth URL'
                      name='baseAuthUrl'
                      defaultValue='https://degreed.com'
                      control={form.control}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormInputRow
                  title='Base API URL'
                  description='The base URL for the API. This is provided by Degreed.'
                  smallFormTitles
                  InputComponent={
                    <Input
                      label='Base API URL'
                      name='baseApiUrl'
                      defaultValue='https://api.degreed.com'
                      control={form.control}
                    />
                  }
                />
              </Grid>
            </Box>
          </Collapse>
          <Divider style={{ marginBottom: spacing(1) }} />
          <Box className={classes.ctnControls} display='flex' justifyContent='flex-end'>
            {checked && (
              <>
                <ActionButton
                  onClick={handleClear}
                  disabled={areActionsDisabled}
                > Clear Content From Degreed
                </ActionButton>
                <ActionButton
                  onClick={handleSynch}
                  disabled={areActionsDisabled}
                > Synchronize Content To Degreed
                </ActionButton>
              </>
            )}
            <ActionButton
              onClick={handleSave}
              disabled={isSaveDisabled}
              variant='primary'
            > Save
            </ActionButton>
          </Box>
        </Box>

      </Grid>
    </Grid>
  )
}

export default DegreedSwitchInput
